import { Link, createFileRoute, Outlet, useRouterState } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { Search, Plus, Calendar, SlidersHorizontal, ArrowRight, Building2, User, Users, Briefcase, ExternalLink, Download, BookOpen } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { PageSkeleton } from '@/components/ui/page-skeleton'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/ui/form'
import { Select } from '@/components/ui/select'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { CreateProjectModal, type ProjectFormData } from '@/components/projects/CreateProjectModal'
import { ProjectStatus, ProjectStatusLabels, enumToSelectOptions } from '@/utils/enum'
import { useAuth } from '@/contexts/AuthContext'
import { useClientPreviewLayout } from '@/contexts/ClientPreviewContext'
import { api } from '@/services/api'
import { fetchClientsWithEmails } from '@/services/clients'
import { exportProjectsToExcel } from '@/utils/exportProjects'
import { getProjectStatusStyle } from '@/lib/projectStatus'
import { HeroBanner } from '@/components/layout/HeroBanner'
import { ClientWorkshopAccessCard } from '@/components/projects/ClientWorkshopAccessCard'

export const Route = createFileRoute('/projects')({
  component: Projects,
})

interface ProjectURL {
  id: number
  url_type: string
  url: string
  created_at: string
}

interface Project {
  id: number
  nom: string
  description: string | null
  date_kickoff: string
  stack_technique: string | null
  noms_devs: string | null
  status: string
  client_id: string
  project_manager_id: string
  activity_sector_id: number | null
  created_by_id: string
  created_at: string
  updated_at: string | null
  urls: ProjectURL[]
  // Enriched data
  client_nom: string | null
  client_email: string | null
  project_manager_nom: string | null
  project_manager_email: string | null
  project_managers?: Array<{ id: string; nom: string | null; email: string | null }>
  activity_sector_nom: string | null
  payment_status_id?: number | null
  payment_status_nom?: string | null
}

function Projects() {
  const router = useRouterState()
  const { user } = useAuth()
  const isChildRoute = router.location.pathname !== '/projects'
  const [searchQuery, setSearchQuery] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const isClient = user?.role === 'client'
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [projects, setProjects] = useState<Project[]>([])
  const [loadingProjects, setLoadingProjects] = useState(false)
  const [clients, setClients] = useState<Array<{ id: string; nom: string; email: string }>>([])
  const [projectManagers, setProjectManagers] = useState<Array<{ id: string; nom: string; email: string }>>([])
  const isSuperAdmin =
    user?.role === 'super_admin' ||
    user?.email?.endsWith('@qolaig.com') ||
    user?.email?.endsWith('@qolaig.fr')
  const [activitySectors, setActivitySectors] = useState<Array<{ id: number; nom: string }>>([])
  const [paymentStatuses, setPaymentStatuses] = useState<Array<{ id: number; nom: string }>>([])
  const [loadingClients, setLoadingClients] = useState(false)
  const [loadingManagers, setLoadingManagers] = useState(false)
  const [loadingSectors, setLoadingSectors] = useState(false)
  const [loadingPaymentStatuses, setLoadingPaymentStatuses] = useState(false)
  const [filters, setFilters] = useState({
    client: '',
    projectManager: '',
    status: '',
    dateFrom: '',
    dateTo: '',
    sector: '',
    paymentStatus: '',
  })
  // Bascule « Vue admin / Vue client » (admins), comme sur la fiche projet :
  // en Vue client, on affiche la liste telle que la voit le client choisi
  const canPreviewClient = user?.role === 'admin' || user?.role === 'super_admin'
  const [viewMode, setViewMode] = useState<'admin' | 'client'>('admin')
  const [previewClientId, setPreviewClientId] = useState('')
  const isPreviewing = canPreviewClient && viewMode === 'client' && previewClientId !== ''
  const previewClient = clients.find((client) => client.id === previewClientId) || null
  const showClientView = isClient || isPreviewing
  const clientViewName = isClient ? user?.nom : previewClient?.nom
  // En Vue client, bandeau client à la place de la sidebar (la fiche gère son propre mode)
  useClientPreviewLayout(canPreviewClient && viewMode === 'client' && !isChildRoute)

  // Fetch projects from API
  const fetchProjects = async () => {
    setLoadingProjects(true)
    try {
      const params: Record<string, string> = {}
      
      // Add search query
      if (searchQuery.trim()) {
        params.nom = searchQuery.trim()
      }
      
      // Add filters (only if they have values)
      const clientIdParam = isPreviewing ? previewClientId : filters.client
      if (clientIdParam && clientIdParam.trim() !== '') {
        params.client_id = clientIdParam
      }
      if (filters.status && filters.status.trim() !== '') {
        params.status = filters.status
      }
      if (filters.dateFrom && filters.dateFrom.trim() !== '') {
        params.date_kickoff_from = filters.dateFrom
      }
      if (filters.dateTo && filters.dateTo.trim() !== '') {
        params.date_kickoff_to = filters.dateTo
      }
      if (!isPreviewing && filters.projectManager && filters.projectManager.trim() !== '') {
        params.project_manager_id = filters.projectManager
      }
      if (!isPreviewing && filters.sector && filters.sector.trim() !== '') {
        params.activity_sector_id = filters.sector
      }
      if (isSuperAdmin && !isPreviewing && filters.paymentStatus && filters.paymentStatus.trim() !== '') {
        params.payment_status_id = filters.paymentStatus
      }

      console.log('Fetching projects with filters:', { filters, params })
      const response = await api.get<{ data: Project[]; count: number; message: string }>('/projects', params)
      console.log('Projects received:', response.data?.data?.length || 0)
      const projectsData = response.data?.data || []
      setProjects(projectsData)
    } catch (error: any) {
      console.error('Erreur lors de la récupération des projets:', error)
      setProjects([])
    } finally {
      setLoadingProjects(false)
    }
  }

  // Fetch clients and project managers for filters
  const fetchClients = async () => {
    setLoadingClients(true)
    try {
      // If user is a client, only show themselves in the filter
      if (user?.role === 'client' && user?.nom) {
        setClients([{ id: user.id, nom: user.nom, email: user.email }])
      } else {
        const clientsData = await fetchClientsWithEmails()
        setClients(clientsData.map((client) => ({
          id: client.id,
          nom: client.nom,
          email: client.emails[0] || '',
        })))
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des clients:', error)
      // Fallback: if client, show themselves
      if (user?.role === 'client' && user?.nom) {
        setClients([{ id: user.id, nom: user.nom, email: user.email }])
      } else {
        setClients([])
      }
    } finally {
      setLoadingClients(false)
    }
  }

  const fetchProjectManagers = async () => {
    setLoadingManagers(true)
    try {
      const response = await api.get<{ data: Array<{ id: string; nom: string; email: string }>; count: number }>('/users/project-managers')
      setProjectManagers(response.data.data || [])
    } catch (error) {
      console.error('Erreur lors de la récupération des chefs de projet:', error)
      setProjectManagers([])
    } finally {
      setLoadingManagers(false)
    }
  }

  const fetchActivitySectors = async () => {
    setLoadingSectors(true)
    try {
      const response = await api.get<{ data: Array<{ id: number; nom: string }>; count: number }>('/activity-sectors')
      setActivitySectors(response.data.data || [])
    } catch (error) {
      console.error('Erreur lors de la récupération des secteurs d\'activité:', error)
      setActivitySectors([])
    } finally {
      setLoadingSectors(false)
    }
  }

  const fetchPaymentStatuses = async () => {
    setLoadingPaymentStatuses(true)
    try {
      const response = await api.get<{ data: Array<{ id: number; nom: string }>; count: number }>('/payment-statuses')
      setPaymentStatuses(response.data.data || [])
    } catch (error) {
      console.error('Erreur lors de la récupération des statuts de paiement:', error)
      setPaymentStatuses([])
    } finally {
      setLoadingPaymentStatuses(false)
    }
  }

  // Fetch clients, project managers and activity sectors on mount
  useEffect(() => {
    if (!isChildRoute) {
      fetchClients()
      // Listes utiles aux seuls filtres admin (masqués côté client)
      if (!isClient) {
        fetchProjectManagers()
        fetchActivitySectors()
      }
      if (isSuperAdmin) {
        fetchPaymentStatuses()
      }
    }
  }, [isChildRoute, isSuperAdmin])

  // Fetch projects on mount and when filters/search change
  useEffect(() => {
    if (!isChildRoute) {
      console.log('Filters changed, triggering fetch:', filters)
      const timeoutId = setTimeout(() => {
        fetchProjects()
      }, searchQuery ? 500 : 0) // Debounce search by 500ms
      
      return () => clearTimeout(timeoutId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChildRoute, searchQuery, previewClientId, filters.client, filters.status, filters.dateFrom, filters.dateTo, filters.projectManager, filters.sector, filters.paymentStatus])

  // Format date for display
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    })
  }

  const activeFiltersCount = Object.entries(filters).filter(
    ([key, value]) =>
      value !== '' &&
      (key !== 'paymentStatus' || (isSuperAdmin && !isPreviewing)) &&
      (!showClientView || !['client', 'projectManager', 'sector'].includes(key))
  ).length

  // Parse stack technique into tags
  const getStackTags = (stackTechnique: string | null): string[] => {
    if (!stackTechnique) return []
    return stackTechnique.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
  }

  // Count developers
  const getDevCount = (nomsDevs: string | null): number => {
    if (!nomsDevs) return 0
    return nomsDevs.split(',').filter(name => name.trim().length > 0).length
  }

  const getUrlByType = (urls: ProjectURL[], type: string): string | null => {
    const normalizedType = type.toLowerCase()
    const match = urls.find((url) => String(url.url_type).toLowerCase() === normalizedType)
    return match?.url || null
  }

  // If we're on a child route (like /projects/$projectId), only render the Outlet
  if (isChildRoute) {
    return <Outlet />
  }

  return (
    <div className="min-h-screen">
      <HeroBanner className="pb-28 pt-10 sm:pt-12">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            {showClientView && clientViewName && (
              <p className="text-sm font-medium text-[#8FE3EE]">Portail client {clientViewName}</p>
            )}
            <h1 className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">Mes projets</h1>
            <p className="mt-2 text-[15px] text-[#C3CCE0]">
              Sélectionnez un projet pour accéder aux détails et métriques
            </p>
          </div>

          {canPreviewClient && (
            <div className="flex flex-wrap items-center justify-end gap-2">
              {viewMode === 'client' && (
                <div className="w-[240px]">
                  <SearchableSelect
                    value={previewClientId}
                    onChange={(value) => {
                      // Les filtres de l'admin fausseraient l'aperçu : on repart de l'écran vierge du client
                      setSearchQuery('')
                      setFilters({ client: '', projectManager: '', status: '', dateFrom: '', dateTo: '', sector: '', paymentStatus: '' })
                      setPreviewClientId(value)
                    }}
                    options={clients.map((client) => ({ value: client.id, label: client.nom }))}
                    placeholder={loadingClients ? 'Chargement...' : 'Choisir un client'}
                    searchPlaceholder="Rechercher un client..."
                    disabled={loadingClients}
                    className="h-9 rounded-lg border-white/20 bg-white/10 text-white focus-visible:ring-brand-bright focus-visible:ring-offset-0"
                  />
                </div>
              )}
              <div role="group" aria-label="Mode d'affichage" className="flex rounded-lg bg-white/10 p-1">
                <button
                  type="button"
                  onClick={() => setViewMode('admin')}
                  aria-pressed={viewMode === 'admin'}
                  className={`rounded-md px-3 py-1 text-sm font-medium transition-colors ${viewMode === 'admin' ? 'bg-white text-brand-navy' : 'text-white/80 hover:text-white'}`}
                >
                  Vue admin
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('client')}
                  aria-pressed={viewMode === 'client'}
                  className={`rounded-md px-3 py-1 text-sm font-medium transition-colors ${viewMode === 'client' ? 'bg-white text-brand-navy' : 'text-white/80 hover:text-white'}`}
                >
                  Vue client
                </button>
              </div>
            </div>
          )}
        </div>
      </HeroBanner>

      <main className="relative mx-auto -mt-[72px] w-full max-w-7xl px-4 pb-16 sm:px-8">
        {/* Barre d'outils : recherche, filtres, export, création */}
        <div className="mb-5 rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
          <div className="flex flex-wrap items-center gap-2.5">
            <div className="min-w-[240px] flex-1">
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher par nom, description, client ou technologie..."
                leftIcon={<Search className="w-5 h-5 text-gray-400" />}
                className="h-11 rounded-xl border-gray-200 bg-gray-50 text-[15px] focus-visible:ring-brand-bright"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              aria-expanded={showFilters}
              className={`h-11 rounded-xl px-4 flex items-center gap-2 ${showFilters ? 'border-brand-navy bg-brand-navy text-white hover:bg-brand-navy/90 hover:text-white' : 'border-gray-300 bg-white text-gray-800 hover:bg-gray-50'}`}
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filtres
              {activeFiltersCount > 0 && (
                <span className={`rounded-full px-1.5 text-xs font-semibold ${showFilters ? 'bg-white text-brand-navy' : 'bg-brand-light text-brand-dark'}`}>
                  {activeFiltersCount}
                </span>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => exportProjectsToExcel(projects)}
              disabled={loadingProjects || projects.length === 0}
              className="h-11 rounded-xl border-gray-300 bg-white px-4 text-gray-800 hover:bg-gray-50 flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Exporter Excel
            </Button>
            {(user?.role === 'admin' || user?.role === 'super_admin') && !isPreviewing && (
              <Button
                onClick={() => setShowCreateModal(true)}
                className="h-11 rounded-xl bg-brand px-4 text-white hover:bg-brand-dark flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Nouveau Projet
              </Button>
            )}
          </div>

          {showFilters && (
            <div className="mt-3 space-y-4 border-t border-gray-100 px-1 pb-1 pt-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                {/* Client, chef de projet et secteur : sans objet pour un client, qui ne voit que ses projets */}
                {!showClientView && (
                <FormField label="Client">
                  <Select
                    value={isPreviewing ? previewClientId : filters.client}
                    onChange={(e) => setFilters({ ...filters, client: e.target.value })}
                    placeholder={loadingClients ? "Chargement..." : "Tous les clients"}
                    options={[
                      { value: '', label: 'Tous les clients' },
                      ...(isPreviewing && previewClient ? [previewClient] : clients).map(client => ({
                        value: client.id,
                        label: client.email ? `${client.nom} (${client.email})` : client.nom
                      }))
                    ]}
                    disabled={loadingClients || isPreviewing}
                    className="h-11 rounded-xl border-gray-200 bg-gray-50 text-[15px]"
                  />
                </FormField>
                )}

                {!showClientView && (
                <FormField label="Chef de projet">
                  <Select
                    value={filters.projectManager}
                    onChange={(e) => setFilters({ ...filters, projectManager: e.target.value })}
                    placeholder={loadingManagers ? "Chargement..." : "Tous les chefs de projet"}
                    options={[
                      { value: '', label: 'Tous les chefs de projet' },
                      ...projectManagers.map(pm => ({
                        value: pm.id,
                        label: `${pm.nom} (${pm.email})`
                      }))
                    ]}
                    disabled={loadingManagers}
                    className="h-11 rounded-xl border-gray-200 bg-gray-50 text-[15px]"
                  />
                </FormField>
                )}

                <FormField label="Statut">
                  <Select
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    options={[
                      { value: '', label: 'Tous les statuts' },
                      ...enumToSelectOptions(ProjectStatus, ProjectStatusLabels)
                    ]}
                    placeholder="Tous les statuts"
                    className="h-11 rounded-xl border-gray-200 bg-gray-50 text-[15px]"
                  />
                </FormField>

                <FormField label="Date de kickoff - Du">
                  <Input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                    className="h-11 rounded-xl border-gray-200 bg-gray-50 text-[15px]"
                  />
                </FormField>

                <FormField label="Date de kickoff - Au">
                  <Input
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                    className="h-11 rounded-xl border-gray-200 bg-gray-50 text-[15px]"
                  />
                </FormField>

                {!showClientView && (
                <FormField label="Secteur d'activité">
                  <Select
                    value={filters.sector}
                    onChange={(e) => setFilters({ ...filters, sector: e.target.value })}
                    options={[
                      { value: '', label: 'Tous les secteurs' },
                      ...activitySectors.map(sector => ({
                        value: String(sector.id),
                        label: sector.nom
                      }))
                    ]}
                    placeholder={loadingSectors ? "Chargement..." : "Tous les secteurs"}
                    disabled={loadingSectors}
                    className="h-11 rounded-xl border-gray-200 bg-gray-50 text-[15px]"
                  />
                </FormField>
                )}

                {isSuperAdmin && !isPreviewing && (
                  <FormField label="Statut de paiement">
                    <Select
                      value={filters.paymentStatus}
                      onChange={(e) => setFilters({ ...filters, paymentStatus: e.target.value })}
                      options={[
                        { value: '', label: 'Tous les statuts' },
                        ...paymentStatuses.map((status) => ({
                          value: String(status.id),
                          label: status.nom,
                        })),
                      ]}
                      placeholder={loadingPaymentStatuses ? 'Chargement...' : 'Tous les statuts'}
                      disabled={loadingPaymentStatuses}
                      className="h-11 rounded-xl border-gray-200 bg-gray-50 text-[15px]"
                    />
                  </FormField>
                )}
              </div>
              {activeFiltersCount > 0 && (
                <button
                  type="button"
                  onClick={() => setFilters({ client: '', projectManager: '', status: '', dateFrom: '', dateTo: '', sector: '', paymentStatus: '' })}
                  className="text-sm font-medium text-brand hover:text-brand-dark"
                >
                  Réinitialiser les filtres
                </button>
              )}
            </div>
          )}
        </div>

        {/* Accès workshop.qolaig.app par équipe (vue client uniquement) */}
        {isClient && <ClientWorkshopAccessCard />}
        {isPreviewing && <ClientWorkshopAccessCard previewClientId={previewClientId} />}

        {/* Projects List */}
        {loadingProjects ? (
          <PageSkeleton variant="list" />
        ) : projects.length === 0 ? (
          <div className="rounded-2xl border border-gray-200 bg-white p-10 text-center">
            <p className="text-gray-700 text-lg font-medium mb-2">Aucun projet trouvé</p>
            {user?.role !== 'client' && !isSuperAdmin && !isPreviewing && (
              <p className="text-gray-500 text-sm mb-2">
                Vous ne voyez que les projets dont vous êtes chef de projet ou développeur.
              </p>
            )}
            <p className="text-sm text-gray-500">
              {searchQuery || Object.values(filters).some(f => f !== '')
                ? "Essayez de modifier vos critères de recherche ou vos filtres"
                : "Créez votre premier projet en cliquant sur le bouton \"Nouveau Projet\""}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {projects.map((project) => {
              const stackTags = getStackTags(project.stack_technique)
              const devCount = getDevCount(project.noms_devs)
              const status = getProjectStatusStyle(project.status)
              const figmaUrl = getUrlByType(project.urls, 'figma')
              const notionUrl = getUrlByType(project.urls, 'notion')

              return (
                <Link
                  to="/projects/$projectId"
                  params={{ projectId: project.id.toString() }}
                  search={isPreviewing ? { vue: 'client' } : {}}
                  key={project.id}
                  className="group flex flex-col gap-4 rounded-[18px] border border-gray-200 bg-white p-6 text-brand-navy transition hover:-translate-y-0.5 hover:border-brand-bright/50 hover:shadow-lg hover:shadow-brand-navy/5"
                >
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${status.pill}`}>
                      <span className={`h-[7px] w-[7px] rounded-full ${status.dot}`} />
                      {status.label}
                    </span>
                  </div>

                  <div>
                    <h3 className="text-[19px] font-semibold leading-snug transition group-hover:text-brand">
                      {project.nom}
                    </h3>
                    <p className={`mt-1.5 line-clamp-3 text-sm leading-relaxed ${project.description ? 'text-gray-600' : 'text-gray-400'}`}>
                      {project.description || 'Aucune description'}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {project.date_kickoff && (
                      <span className="inline-flex items-center gap-1.5 rounded-[10px] bg-brand-canvas px-2.5 py-1.5 text-[13px] text-gray-700">
                        <Calendar className="w-3.5 h-3.5 text-brand" />
                        {formatDate(project.date_kickoff)}
                      </span>
                    )}
                    {project.project_manager_nom && (
                      <span className="inline-flex items-center gap-1.5 rounded-[10px] bg-brand-canvas px-2.5 py-1.5 text-[13px] text-gray-700">
                        <User className="w-3.5 h-3.5 text-brand" />
                        {project.project_managers?.length
                          ? project.project_managers.map((pm) => pm.nom || pm.email).filter(Boolean).join(', ')
                          : project.project_manager_nom}
                      </span>
                    )}
                    {project.client_nom && (
                      <span className="inline-flex items-center gap-1.5 rounded-[10px] bg-brand-canvas px-2.5 py-1.5 text-[13px] text-gray-700">
                        <Building2 className="w-3.5 h-3.5 text-brand-violet" />
                        {project.client_nom}
                      </span>
                    )}
                    {!showClientView && project.activity_sector_nom && (
                      <span className="inline-flex items-center gap-1.5 rounded-[10px] bg-brand-canvas px-2.5 py-1.5 text-[13px] text-gray-700">
                        <Briefcase className="w-3.5 h-3.5 text-amber-600" />
                        {project.activity_sector_nom}
                      </span>
                    )}
                    {!showClientView && devCount > 0 && (
                      <span className="inline-flex items-center gap-1.5 rounded-[10px] bg-brand-canvas px-2.5 py-1.5 text-[13px] text-gray-700">
                        <Users className="w-3.5 h-3.5 text-gray-500" />
                        {devCount} {devCount > 1 ? 'Membres' : 'Membre'}
                      </span>
                    )}
                  </div>

                  <div className="mt-auto flex flex-wrap items-center gap-1.5 border-t border-gray-100 pt-4">
                    {showClientView && figmaUrl && (
                      <a
                        href={figmaUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 rounded-lg bg-brand-violet-light px-2.5 py-1 text-xs font-medium text-brand-violet-dark hover:bg-[#E6DFFF]"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Figma
                      </a>
                    )}
                    {showClientView && notionUrl && (
                      <a
                        href={notionUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 rounded-lg bg-[#FFF4E5] px-2.5 py-1 text-xs font-medium text-[#93530B] hover:bg-[#FFEACC]"
                      >
                        <BookOpen className="w-3 h-3" />
                        Notion
                      </a>
                    )}
                    {stackTags.slice(0, 3).map((tag, i) => (
                      <span key={i} className="rounded-lg bg-brand-light px-2.5 py-1 text-xs text-brand-dark">
                        {tag}
                      </span>
                    ))}
                    {stackTags.length > 3 && (
                      <span className="rounded-lg bg-brand-canvas px-2.5 py-1 text-xs text-gray-600">
                        +{stackTags.length - 3}
                      </span>
                    )}
                    <span className="ml-auto flex h-[34px] w-[34px] items-center justify-center rounded-full bg-brand-navy text-white transition group-hover:bg-brand">
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              )
            })}
          </div>
        )}
      </main>

      {/* Create Project Modal */}
      <CreateProjectModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        onSubmit={async (data: ProjectFormData) => {
          setIsCreating(true)
          try {
            // Transform form data to match backend API payload
            const payload: any = {
              nom: data.nomProjet,
              date_kickoff: data.dateKickoff,
            }

            // Add optional fields only if they have values
            if (data.description?.trim()) {
              payload.description = data.description.trim()
            }
            // Handle description_interne (only for super admins)
            const isSuperAdmin = user?.email?.endsWith('@qolaig.com') || user?.email?.endsWith('@qolaig.fr')
            if (isSuperAdmin && data.descriptionInterne?.trim()) {
              payload.description_interne = data.descriptionInterne.trim()
            }
            if (isSuperAdmin && data.visibility) {
              payload.visibility = data.visibility
            }
            if (data.stackTechnique?.trim()) {
              payload.stack_technique = data.stackTechnique.trim()
            }
            if (data.developerIds) {
              payload.developer_ids = data.developerIds
            }
            if (data.developpeurs?.trim()) {
              payload.noms_devs = data.developpeurs.trim()
            }

            // Client existant : identifiant Client, sans renvoyer les emails.
            if (data.clientId && data.clientNom) {
              const clientIdNumber = Number(data.clientId)
              if (Number.isInteger(clientIdNumber)) {
                payload.client_id = clientIdNumber
              } else if (data.clientEmails && data.clientEmails.length > 0) {
                payload.client = {
                  nom: data.clientNom.trim(),
                  emails: data.clientEmails.filter(e => e && e.trim()).map(e => e.trim())
                }
              } else if (data.clientEmailExisting) {
                payload.client = {
                  nom: data.clientNom.trim(),
                  email: data.clientEmailExisting.trim()
                }
              } else {
                throw new Error('Email du client manquant')
              }
            } else if (data.clientName?.trim()) {
              // New client - support multiple emails
              const emails = data.clientEmails?.filter(e => e && e.trim()) || []
              if (emails.length > 0) {
                // Multiple emails - all can login
                payload.client = {
                  nom: data.clientName.trim(),
                  emails: emails.map(e => e.trim())
                }
              } else if (data.clientEmail?.trim()) {
                // Single email (backward compatible)
                payload.client = {
                  nom: data.clientName.trim(),
                  email: data.clientEmail.trim()
                }
              } else {
                throw new Error('Veuillez entrer au moins un email pour le client')
              }
            } else {
              throw new Error('Veuillez sélectionner ou créer un client')
            }

            // Handle project manager - either existing (by ID) or new (by object)
            if (data.projectManagerIds?.length) {
              payload.project_manager_ids = data.projectManagerIds
              payload.project_manager_id = data.projectManagerIds[0]
            } else if (data.projectManagerId) {
              payload.project_manager_id = data.projectManagerId
            } else if (data.projectManagerName?.trim() && data.projectManagerEmail?.trim()) {
              // New project manager
              payload.project_manager = {
                nom: data.projectManagerName.trim(),
                email: data.projectManagerEmail.trim()
              }
            } else {
              throw new Error('Veuillez sélectionner ou créer un chef de projet')
            }

            // Handle URLs - support multiple URLs for each type
            const githubUrls = (data.urlsGithub || []).filter(u => u && u.trim()).map(u => u.trim())
            const serverUrls = (data.urlsServeur || []).filter(u => u && u.trim()).map(u => u.trim())
            const figmaUrls = (data.urlsFigma || []).filter(u => u && u.trim()).map(u => u.trim())
            const notionUrls = (data.urlsNotion || []).filter(u => u && u.trim()).map(u => u.trim())
            
            // Fallback to single URL if arrays are empty (backward compatible)
            if (githubUrls.length > 0) {
              payload.github_urls = githubUrls
            } else if (data.urlGithub?.trim()) {
              payload.github_urls = [data.urlGithub.trim()]
            }
            if (serverUrls.length > 0) {
              payload.server_urls = serverUrls
            } else if (data.urlServeur?.trim()) {
              payload.server_urls = [data.urlServeur.trim()]
            }
            if (figmaUrls.length > 0) {
              payload.figma_urls = figmaUrls
            } else if (data.urlFigma?.trim()) {
              payload.figma_urls = [data.urlFigma.trim()]
            }
            if (notionUrls.length > 0) {
              payload.notion_urls = notionUrls
            }

            // Handle activity sector if provided (convert to int or null)
            // Only send if it's a valid number
            if (data.activitySectorId && data.activitySectorId.trim() !== '') {
              const sectorId = parseInt(data.activitySectorId)
              if (!isNaN(sectorId) && sectorId > 0) {
                payload.activity_sector_id = sectorId
              }
            }

            console.log('Payload envoyé:', JSON.stringify(payload, null, 2))

            // Make API call
            const response = await api.post('/projects', payload)
            console.log('Projet créé:', response.data)
            
            // Close modal on success
            setShowCreateModal(false)
            // Refresh projects list
            await fetchProjects()
          } catch (error: any) {
            console.error('Erreur lors de la création du projet:', error)
            console.error('Détails de l\'erreur:', error?.response?.data)
            // Show error message to user
            let errorMessage = 'Une erreur s\'est produite lors de la création du projet'
            if (error?.message) {
              errorMessage = error.message
            } else if (error?.response?.data?.detail) {
              if (Array.isArray(error.response.data.detail)) {
                errorMessage = error.response.data.detail.map((d: any) => {
                  const field = Array.isArray(d.loc) ? d.loc.slice(-1)[0] : ''
                  return field ? `${field}: ${d.msg || d.message || 'Erreur de validation'}` : (d.msg || d.message || 'Erreur de validation')
                }).join(', ')
              } else {
                errorMessage = error.response.data.detail
              }
            }
            alert(errorMessage) // TODO: Replace with proper notification component
          } finally {
            setIsCreating(false)
          }
        }}
        isLoading={isCreating}
      />
    </div>
  )
}
